// FPS overlay (USER GATE "FPS ≥ 55"): rAF-counted, redrawn at `hz` (2 Hz). Plain DOM, pointer-transparent.

export interface FpsOverlay {
  /** Last measured average FPS over the update window. */
  fps(): number;
  /** Worst (lowest) instantaneous FPS in the last window. */
  low(): number;
  destroy(): void;
}

export interface FpsOverlayConfig {
  /** Redraw rate of the readout (Hz). */
  hz: number;
  /** Green at or above (the USER GATE bar, 55). */
  good: number;
  /** Amber at or above; red below. */
  warn: number;
}

export function createFpsOverlay(doc: Document, cfg: FpsOverlayConfig): FpsOverlay {
  const { hz } = cfg;
  const el = doc.createElement('div');
  el.id = 'gm-fps';
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText =
    'position:fixed;right:max(4px,env(safe-area-inset-right));top:max(4px,env(safe-area-inset-top));z-index:90;' +
    'pointer-events:none;padding:2px 6px;border-radius:6px;background:rgba(0,0,0,.55);color:#9f9;' +
    'font:600 11px/1.3 ui-monospace,monospace;font-variant-numeric:tabular-nums;';
  doc.body.appendChild(el);
  const windowMs = 1000 / hz;
  let frames = 0;
  let start = performance.now();
  let last = start;
  let worstDt = 0;
  let fps = 0;
  let low = 0;
  let raf = 0;
  const tick = (now: number): void => {
    frames += 1;
    worstDt = Math.max(worstDt, now - last);
    last = now;
    if (now - start >= windowMs) {
      fps = (frames * 1000) / (now - start);
      low = worstDt > 0 ? 1000 / worstDt : fps;
      el.textContent = `${fps.toFixed(0)} fps · min ${low.toFixed(0)}`;
      el.style.color = fps >= cfg.good ? '#9f9' : fps >= cfg.warn ? '#fd6' : '#f77';
      frames = 0;
      worstDt = 0;
      start = now;
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return {
    fps: () => fps,
    low: () => low,
    destroy() {
      cancelAnimationFrame(raf);
      el.remove();
    },
  };
}
